import { ReactNode, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { OfflineBanner } from "./OfflineBanner";
import { CleanerBottomNav } from "./CleanerBottomNav";
import { PullToRefresh } from "./PullToRefresh";
import { useViewContext } from "@/contexts/ViewContext";
import { useTranslation } from "@/hooks/useTranslation";
import { isCleanerJobRunnerPath } from "@/lib/turnoverChrome";

interface AppLayoutProps {
  children: ReactNode;
}

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { viewContext } = useViewContext();
  const { isRtl } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isCleanerContext = viewContext === "cleaner";
  const onJobRunner = isCleanerJobRunnerPath(location.pathname);
  const showCleanerNav = isCleanerContext && !onJobRunner;
  const [inputFocused, setInputFocused] = useState(false);

  const onRefresh = useCallback(async () => {
    await queryClient.refetchQueries({ type: "active" });
  }, [queryClient]);

  return (
    <div
      className="min-h-screen bg-[#f4f7f9] overflow-x-hidden min-w-0"
      dir={isCleanerContext && isRtl ? "rtl" : "ltr"}
      onFocusCapture={(e) => {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
          setInputFocused(true);
        }
      }}
      onBlurCapture={() => setInputFocused(false)}
    >
      <OfflineBanner />
      <PullToRefresh disabled={onJobRunner || inputFocused} onRefresh={onRefresh}>
        {children}
      </PullToRefresh>
      {showCleanerNav ? <CleanerBottomNav /> : null}
      {showCleanerNav ? (
        <div
          className="md:hidden"
          style={{ height: "calc(4rem + env(safe-area-inset-bottom, 0px))" }}
          aria-hidden
        />
      ) : null}
    </div>
  );
};
